import React from 'react';
import {
  Heart, AlertTriangle, Eye, ShieldCheck, Activity, TrendingUp, Zap, ArrowRight, CheckCircle2,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/api';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from 'recharts';
import EngineWeakLinkCallout from '../engines/EngineWeakLinkCallout';
import EngineActionPlanCard from '../engines/EngineActionPlanCard';
import { WeakestMeasureTile } from '../health/HealthActionSpine';
import PeerBenchmarkBar from '../common/PeerBenchmarkBar';
import PeerBenchmarkTrend from '../common/PeerBenchmarkTrend';
import TransferHealthChain from './TransferHealthChain';

const barColor = (s) => (s >= 75 ? '#06b6d4' : s >= 60 ? '#14b8a6' : s >= 45 ? '#f59e0b' : '#f43f5e');
const barColorLight = (s) => (s >= 75 ? '#0e7490' : s >= 60 ? '#0f766e' : s >= 45 ? '#b45309' : '#be123c');
const band = (s) => (s >= 75 ? 'Strong' : s >= 50 ? 'Healthy' : s >= 25 ? 'At-Risk' : 'Critical');
const scoreText = (s) => (s >= 75 ? 'text-cyan-300' : s >= 60 ? 'text-teal-300' : s >= 45 ? 'text-amber-300' : 'text-rose-300');
const bandChip = (s) => (
  s >= 75 ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'
    : s >= 50 ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
      : s >= 25 ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
        : 'bg-rose-500/10 border-rose-500/30 text-rose-300'
);
const COMP_ICONS = { Capability: Activity, Motivation: Zap, 'L3 Behavior': TrendingUp };
export const formatPeriod = (p) => {
  const m = /^(\d{4})-(\d{2})$/.exec(p || '');
  if (!m) return p || '';
  return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

export function DarkCard({ className = '', children, testId }) {
  return <div className={cn('rounded-2xl bg-[#111827] ring-1 ring-[#1f2a44] shadow-xl', className)} data-testid={testId}>{children}</div>;
}

export function ScoreGauge({ score, size = 80 }) {
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r="42" fill="none" stroke="#1f2a44" strokeWidth="8" />
        <circle cx="50" cy="50" r="42" fill="none" stroke={barColor(score)} strokeWidth="8"
          strokeDasharray={`${Math.max(0, Math.min(100, score)) * 2.64} 264`} strokeLinecap="round" />
      </svg>
      <div className={cn('absolute inset-0 flex items-center justify-center text-lg font-bold', scoreText(score))}>{Math.round(score)}%</div>
    </div>
  );
}

function KpiTiles({ roeActualPct, components, strongest, topDeficiency, watchArea, peer, navigate }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="roe-kpi-tiles">
      <DarkCard className="p-5" testId="roe-kpi-actual">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Actual ROE</div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className={cn('text-4xl font-heading font-extrabold tabular-nums', scoreText(roeActualPct))}>{Math.round(roeActualPct)}</span>
              <span className="text-sm text-slate-500 font-semibold">%</span>
            </div>
            <span className={cn('mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border', bandChip(roeActualPct))}>{band(roeActualPct)} Transfer</span>
          </div>
          <ScoreGauge score={roeActualPct} />
        </div>
      </DarkCard>

      {topDeficiency ? (
        <DarkCard className="p-5 relative overflow-hidden ring-1 ring-rose-500/40 mc-priority-glass shadow-lg shadow-rose-900/20" testId="roe-kpi-attention">
          <div className="absolute -top-6 -right-6 h-20 w-20 rounded-full bg-rose-500/10 blur-2xl" />
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-rose-300"><AlertTriangle className="h-3.5 w-3.5 text-rose-400" /> Top Deficiency</div>
          <p className="mt-2 text-2xl font-extrabold text-white leading-tight" title={topDeficiency.area}>{topDeficiency.area}</p>
          {topDeficiency.score != null && <p className="mt-1 text-sm text-rose-300 font-semibold tabular-nums">Score {Math.round(topDeficiency.score)}<span className="text-slate-400 font-normal"> / 100</span></p>}
          <p className="text-xs text-slate-400 mt-0.5 capitalize">{String(topDeficiency.severity || 'moderate').toLowerCase()} severity</p>
          {navigate && (
            <button onClick={() => navigate('/roe-health/priorities')} data-testid="roe-kpi-attention-action"
              className="mt-4 w-full justify-center inline-flex items-center gap-2 text-sm font-bold px-3 py-2.5 rounded-lg bg-rose-500 text-white mc-priority-cta hover:bg-rose-400 shadow-lg shadow-rose-500/30 transition-colors">
              <Zap className="h-4 w-4" /> Take Action <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </DarkCard>
      ) : watchArea ? (
        <DarkCard className="p-5 relative overflow-hidden ring-1 ring-amber-500/40 mc-priority-glass shadow-lg shadow-rose-900/20" testId="roe-kpi-attention">
          <div className="absolute -top-6 -right-6 h-20 w-20 rounded-full bg-amber-500/10 blur-2xl" />
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-300"><Eye className="h-3.5 w-3.5 text-amber-400" /> Watch Area</div>
          <p className="mt-2 text-2xl font-extrabold text-white leading-tight" title={watchArea.area}>{watchArea.area}</p>
          {watchArea.score != null && <p className="mt-1 text-sm text-amber-300 font-semibold tabular-nums">Score {Math.round(watchArea.score)}<span className="text-slate-400 font-normal"> / 100</span></p>}
          <p className="text-xs text-slate-400 mt-0.5">Relative weak link</p>
          {navigate && (
            <button onClick={() => navigate('/roe-health/priorities')} data-testid="roe-kpi-attention-action"
              className="mt-4 w-full justify-center inline-flex items-center gap-2 text-sm font-bold px-3 py-2.5 rounded-lg bg-rose-500 text-white mc-priority-cta hover:bg-rose-400 shadow-lg shadow-rose-500/30 transition-colors">
              <Zap className="h-4 w-4" /> Take Action <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </DarkCard>
      ) : (
        <DarkCard className="p-5" testId="roe-kpi-attention">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> No Deficiencies
          </div>
          <p className="mt-2 text-lg font-bold text-slate-100 leading-tight truncate">All Healthy</p>
          <p className="mt-2 text-sm text-emerald-300 font-semibold">Maintain the discipline</p>
        </DarkCard>
      )}

      <DarkCard className="p-5" testId="roe-kpi-strongest">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500"><Heart className="h-3.5 w-3.5 text-emerald-400" /> Strongest Component</div>
        <p className="mt-2 text-lg font-bold text-slate-100 leading-tight">{strongest?.name || '—'}</p>
        {strongest && <p className="mt-2 text-sm text-emerald-300 font-semibold tabular-nums">Score: {Math.round(strongest.score)}%</p>}
      </DarkCard>

      {components.slice(0, 2).map((c) => (
        <DarkCard className="p-5" testId={`roe-kpi-comp-${c.name.replace(/\s+/g, '-').toLowerCase()}`} key={c.name}>
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{c.name}</div>
          <p className={cn('mt-2 text-3xl font-heading font-extrabold tabular-nums', scoreText(c.score))}>{Math.round(c.score)}%</p>
          <p className="text-xs text-slate-400 mt-1">{(c.count || 0).toLocaleString()} {c.unit}</p>
        </DarkCard>
      ))}
    </div>
  );
}

function ComponentBreakdown({ components }) {
  const rows = [...components].sort((a, b) => b.score - a.score);
  const weakId = rows.length ? rows[rows.length - 1].name : null;
  return (
    <DarkCard className="p-5" testId="roe-component-breakdown">
      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Behavior Component Breakdown</div>
      <p className="text-xs text-slate-500 mt-1 mb-4">Actual transfer scores from L2 assessments, motivation surveys and L3 observations</p>
      <div className="space-y-1">
        {rows.map((c) => {
          const Icon = COMP_ICONS[c.name] || Activity;
          const col = barColor(c.score);
          const isWeak = c.name === weakId;
          return (
            <div key={c.name} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl" data-testid={`roe-comp-row-${c.name.replace(/\s+/g, '-').toLowerCase()}`}>
              <span className="shrink-0 w-8 h-8 rounded-lg bg-[#0b1220] ring-1 ring-[#22304d] flex items-center justify-center"><Icon className="h-4 w-4 text-[#44b6d2]" /></span>
              <div className="min-w-0 w-36 sm:w-44 shrink-0">
                <p className="text-sm font-semibold text-slate-100 truncate">{c.name}</p>
                <p className="text-[11px] font-medium mc-band" style={{ color: col, '--bl': barColorLight(c.score) }}>{band(c.score)} · {(c.count || 0).toLocaleString()} {c.unit}</p>
              </div>
              <div className="flex-1 h-2 rounded-full bg-[#0b1220] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${c.score}%`, backgroundColor: col }} /></div>
              {isWeak && <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">Weakest</span>}
              <span className="shrink-0 w-8 text-right text-lg font-bold tabular-nums mc-band" style={{ color: col, '--bl': barColorLight(c.score) }}>{Math.round(c.score)}</span>
            </div>
          );
        })}
      </div>
    </DarkCard>
  );
}

function AlertsStrip({ deficiencies, navigate }) {
  const cells = (deficiencies || []).slice(0, 4).map((d) => ({ label: d.area, sub: `${d.severity} severity`, value: d.score != null ? `${Math.round(d.score)}%` : '—', tone: d.severity === 'critical' || d.severity === 'high' ? 'text-rose-300' : 'text-amber-300' }));
  const allClear = cells.length === 0;
  return (
    <DarkCard className="px-5 py-4" testId="roe-recent-alerts">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <span className={cn('w-8 h-8 rounded-lg ring-1 flex items-center justify-center', allClear ? 'bg-emerald-500/15 ring-emerald-500/30' : 'bg-rose-500/15 ring-rose-500/30')}>
            {allClear ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-rose-400" />}
          </span>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-300">Behavior Alerts</p>
            <p className="text-xs text-slate-500">{allClear ? 'No transfer deficiencies flagged.' : 'Some transfer gaps need attention.'}</p>
          </div>
        </div>
        <button onClick={() => navigate('/roe-health/variance')} className="text-xs font-semibold text-[#44b6d2] hover:text-cyan-300 inline-flex items-center gap-1 transition-colors" data-testid="roe-alerts-view-all">View variance <ArrowRight className="h-3.5 w-3.5" /></button>
        <div className="flex-1 grid grid-cols-2 lg:grid-cols-4 gap-3 min-w-[260px]">
          {cells.map((c, i) => (
            <div key={i} className="rounded-xl bg-[#0b1220] ring-1 ring-[#22304d] px-3 py-2" data-testid={`roe-alert-cell-${i}`}>
              <p className="text-[11px] font-semibold text-slate-200 truncate">{c.label}</p>
              <p className="text-[10px] text-slate-500 truncate">{c.sub}</p>
              <p className={cn('text-sm font-bold mt-0.5', c.tone)}>{c.value}</p>
            </div>
          ))}
        </div>
      </div>
    </DarkCard>
  );
}

const ROE_METRIC_KEYS = { Capability: 'capability', Motivation: 'motivation', 'L3 Behavior': 'l3_behavior', 'PE Score': 'pe_score', ROE: 'roe' };

const QUALITY_CHIP = {
  High: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
  Moderate: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  Low: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  None: 'bg-white/5 text-slate-400 border-white/10',
};
export function EvidenceQualityChip({ quality, compact = false }) {
  if (!quality) return null;
  const n = quality.total_sample || 0;
  const title = `Evidence quality ${quality.score}/100 · ${quality.sources} source${quality.sources === 1 ? '' : 's'} (${(quality.source_list || []).join(', ') || 'none'}) · n=${n.toLocaleString()}`;
  return (
    <span
      className={cn('inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full border', QUALITY_CHIP[quality.label] || QUALITY_CHIP.None)}
      title={title}
      data-testid="evidence-quality-chip"
    >
      <ShieldCheck className="h-2.5 w-2.5" />
      {compact ? quality.label : `${quality.label} evidence · ${quality.sources} src · n=${n.toLocaleString()}`}
    </span>
  );
}
// Per-cohort transfer breakdown — Capability → Confidence → Application → Behavior,
// reusing the /roe/actuals/cohort-breakdown endpoint (now chain-aware).
function CohortScoreCell({ v, measured = true, prev, compare }) {
  if (v == null || measured === false) return <td className="py-2 px-2 text-center text-slate-600">—</td>;
  const d = compare && typeof prev === 'number' ? Math.round(v - prev) : null;
  return (
    <td className="py-2 px-2 text-center whitespace-nowrap">
      <span className={cn('font-semibold tabular-nums', scoreText(v))}>{Math.round(v)}</span>
      {compare && (
        d == null
          ? <span className="ml-1 text-[10px] text-slate-600">·</span>
          : <span className={cn('ml-1 text-[10px] font-bold tabular-nums', d > 0 ? 'text-emerald-300' : d < 0 ? 'text-rose-300' : 'text-slate-500')} data-testid="cohort-delta">{d > 0 ? '▲' : d < 0 ? '▼' : '▬'}{Math.abs(d)}</span>
      )}
    </td>
  );
}
// Where each missing signal gets fixed. KPI actuals live in Data Management;
// the transfer signals are imported on Transfer Health › Actuals.
export const fixLinkFor = (signal, cohortId, cohortName) => {
  const q = `cohort=${encodeURIComponent(cohortId || '')}&cohort_name=${encodeURIComponent(cohortName || '')}`;
  if (signal === 'kpi') return `/data-management?tab=kpi&${q}`;
  const type = { capability: 'l2', confidence: 'motivation', application: 'evidence' }[signal];
  return type ? `/roe-health/deep-dives?import=${type}&${q}` : null;
};
export function CompletenessBadge({ c, compact = false, cohortId, cohortName, navigate }) {
  if (!c) return null;
  const missing = c.missing_labels || [];
  const missingKeys = c.missing || [];
  const ok = missing.length === 0;
  const tone = ok ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' : missing.length >= 3 ? 'bg-rose-500/10 text-rose-300 border-rose-500/30' : 'bg-amber-500/10 text-amber-300 border-amber-500/30';
  return (
    <span className="inline-flex items-center gap-1 flex-wrap" data-testid="completeness-badge" title={ok ? 'Capability, Confidence, Application and KPI signals all present' : `Missing: ${missing.join(', ')}`}>
      <span className={cn('inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full border tabular-nums', tone)}>{c.score}/4</span>
      {!compact && !ok && missingKeys.map((k, i) => {
        const href = fixLinkFor(k, cohortId, cohortName);
        const label = `no ${missing[i] || k}`;
        return href && navigate ? (
          <button
            key={k}
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(href); }}
            className="text-[10px] px-1.5 py-0.5 rounded-full border border-white/10 bg-white/5 text-slate-300 hover:bg-cyan-500/15 hover:text-cyan-200 hover:border-cyan-500/30 transition-colors"
            title={`Import ${missing[i] || k} data for this cohort`}
            data-testid={`fix-link-${k}`}
          >
            {label} ↗
          </button>
        ) : <span key={k} className="text-[10px] text-slate-400">{label}</span>;
      })}
    </span>
  );
}
const CELLS = ['capability', 'confidence', 'application', 'l3', 'roe'];
const movement = (r) => CELLS.reduce((m, k) => {
  const a = r[k]?.actual, b = r.prev?.[k]?.actual;
  return typeof a === 'number' && typeof b === 'number' ? Math.max(m, Math.abs(a - b)) : m;
}, 0);
export function CohortTransferTable({ selectedOrg, topN = 10, period = null, onClearPeriod, onPeriodChange, navigate }) {
  const [rows, setRows] = React.useState(null);
  const [prevRows, setPrevRows] = React.useState(null);
  const [periods, setPeriods] = React.useState([]);
  const [periodA, setPeriodA] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [showAll, setShowAll] = React.useState(false);
  const [sortMoved, setSortMoved] = React.useState(false);
  const orgParam = selectedOrg && selectedOrg !== 'all' ? selectedOrg : null;

  // Available months (for the A/B pickers). Default A = month before B.
  React.useEffect(() => {
    let alive = true;
    const params = new URLSearchParams();
    if (orgParam) params.set('organization_id', orgParam);
    api.get(`/roe/actuals/filters?${params.toString()}`)
      .then((res) => { if (alive) setPeriods(res.data?.periods || []); })
      .catch(() => { if (alive) setPeriods([]); });
    return () => { alive = false; };
  }, [orgParam]);
  React.useEffect(() => {
    if (!period || !periods.length) { setPeriodA(''); return; }
    const idx = periods.indexOf(period);
    setPeriodA(idx > 0 ? periods[idx - 1] : '');
  }, [period, periods]);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    const fetchRows = (p) => {
      const params = new URLSearchParams();
      if (orgParam) params.set('organization_id', orgParam);
      if (p) params.set('period', p);
      return api.get(`/roe/actuals/cohort-breakdown?${params.toString()}`).then((res) => res.data?.cohorts || []).catch(() => []);
    };
    Promise.all([fetchRows(period), period && periodA ? fetchRows(periodA) : Promise.resolve(null)])
      .then(([cur, prev]) => { if (alive) { setRows(cur); setPrevRows(prev); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [orgParam, period, periodA]);

  const compare = !!(period && periodA && prevRows);
  const pickers = periods.length >= 2 && (
    <div className="flex items-center gap-1.5 text-[11px]" data-testid="cohort-period-pickers">
      <select value={periodA} onChange={(e) => setPeriodA(e.target.value)} disabled={!period} className="rounded-md border border-white/10 bg-[#0b1220] text-slate-200 px-1.5 py-0.5 text-[11px] disabled:opacity-40" aria-label="Compare from month" data-testid="cohort-period-a">
        <option value="">No compare</option>
        {periods.filter((p) => p !== period).map((p) => <option key={p} value={p}>{formatPeriod(p)}</option>)}
      </select>
      <span className="text-slate-500">vs</span>
      <select value={period || ''} onChange={(e) => onPeriodChange?.(e.target.value || null)} className="rounded-md border border-cyan-500/30 bg-[#0b1220] text-cyan-200 px-1.5 py-0.5 text-[11px]" aria-label="Month to show" data-testid="cohort-period-b">
        <option value="">All periods</option>
        {periods.map((p) => <option key={p} value={p}>{formatPeriod(p)}</option>)}
      </select>
    </div>
  );

  if (loading) return null;
  if (!rows || rows.length === 0) {
    if (!period) return null;
    return (
      <DarkCard className="p-5" testId="cohort-transfer-table-empty">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-slate-300">No cohort actuals recorded for <span className="font-semibold text-slate-100">{formatPeriod(period)}</span>.</p>
          {pickers}
        </div>
        {onClearPeriod && <button onClick={onClearPeriod} className="mt-2 text-[11px] font-semibold text-cyan-300 hover:text-cyan-200" data-testid="cohort-period-clear">Show all periods</button>}
      </DarkCard>
    );
  }
  const prevById = Object.fromEntries((prevRows || []).map((r) => [r.cohort_id, r]));
  const merged = rows.map((r) => ({ ...r, prev: prevById[r.cohort_id] || null }));
  // Cohorts with a measured Confidence signal (the full CAB story) sort first;
  // "Moved most" sorts by the largest absolute change vs the compare month.
  const sorted = [...merged].sort((a, b) => (sortMoved && compare)
    ? movement(b) - movement(a)
    : ((Number(!!b.confidence?.measured) - Number(!!a.confidence?.measured)) || ((b.roe?.actual || 0) - (a.roe?.actual || 0))));
  const incomplete = merged.filter((r) => r.completeness && !r.completeness.complete).length;
  // Disambiguate duplicate cohort NAMES (distinct ids) with a clean numeric suffix.
  const nameCounts = sorted.reduce((m, r) => { m[r.cohort_name] = (m[r.cohort_name] || 0) + 1; return m; }, {});
  const dupSeen = {};
  const labelById = {};
  sorted.forEach((r) => {
    if (nameCounts[r.cohort_name] > 1) {
      dupSeen[r.cohort_name] = (dupSeen[r.cohort_name] || 0) + 1;
      labelById[r.cohort_id] = `${r.cohort_name} (${dupSeen[r.cohort_name]})`;
    } else {
      labelById[r.cohort_id] = r.cohort_name;
    }
  });
  const visible = showAll ? sorted : sorted.slice(0, topN);
  const hasMore = sorted.length > topN;
  return (
    <DarkCard className="p-5" testId="cohort-transfer-table">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Transfer by Cohort</p>
          <p className="text-[11px] text-slate-500">Capability → Confidence → Application → Behavior per cohort{compare ? ` · ▲▼ vs ${formatPeriod(periodA)}` : ''}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {pickers}
          {compare && (
            <button onClick={() => setSortMoved((s) => !s)} className={cn('px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors', sortMoved ? 'bg-cyan-500/15 border-cyan-500/30 text-cyan-200' : 'border-white/10 bg-white/5 text-slate-300 hover:text-white')} data-testid="cohort-sort-moved">
              {sortMoved ? 'Sorted: moved most' : 'Sort: moved most'}
            </button>
          )}
          {period && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-cyan-500/10 border-cyan-500/30 text-cyan-300" data-testid="cohort-period-chip">
              {formatPeriod(period)}
              {onClearPeriod && <button onClick={onClearPeriod} className="hover:text-white" aria-label="Clear period filter" data-testid="cohort-period-clear">×</button>}
            </span>
          )}
          {incomplete > 0 && <span className="text-[11px] text-amber-300 tabular-nums" data-testid="cohort-incomplete-count">{incomplete} incomplete</span>}
          <span className="text-[11px] text-slate-500 tabular-nums">{sorted.length} cohorts</span>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[#111827] z-10">
            <tr className="text-[11px] uppercase tracking-wide text-slate-500 border-b border-white/10">
              <th className="text-left py-2 px-2 font-semibold">Cohort</th>
              <th className="py-2 px-2 font-semibold">Capability</th>
              <th className="py-2 px-2 font-semibold">Confidence</th>
              <th className="py-2 px-2 font-semibold">Application</th>
              <th className="py-2 px-2 font-semibold">Behavior</th>
              <th className="py-2 px-2 font-semibold">Overall</th>
              <th className="py-2 px-2 font-semibold text-left">Data</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.cohort_id} className="border-b border-white/5" data-testid={`cohort-transfer-row-${r.cohort_id}`}>
                <td className="py-2 px-2 text-slate-200 font-medium whitespace-nowrap">{labelById[r.cohort_id]}</td>
                <CohortScoreCell v={r.capability?.actual} measured={r.capability?.measured} prev={r.prev?.capability?.actual} compare={compare} />
                <CohortScoreCell v={r.confidence?.actual} measured={r.confidence?.measured} prev={r.prev?.confidence?.actual} compare={compare} />
                <CohortScoreCell v={r.application?.actual} measured={r.application?.measured} prev={r.prev?.application?.actual} compare={compare} />
                <CohortScoreCell v={r.l3?.actual} measured={r.l3?.measured} prev={r.prev?.l3?.actual} compare={compare} />
                <CohortScoreCell v={r.roe?.actual} prev={r.prev?.roe?.actual} compare={compare} />
                <td className="py-2 px-2"><CompletenessBadge c={r.completeness} cohortId={r.cohort_id} cohortName={labelById[r.cohort_id]} navigate={navigate} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-2 gap-3">
        <p className="text-[10px] text-slate-500">Confidence = learner confidence-to-apply · Application = on-the-job skill application — both measured from survey data. Data = signals present out of Capability, Confidence, Application, KPI.</p>
        {hasMore && (
          <button onClick={() => setShowAll((s) => !s)} data-testid="cohort-transfer-toggle"
            className="shrink-0 text-[11px] font-semibold text-cyan-300 hover:text-cyan-200 transition-colors">
            {showAll ? 'Show less' : `Show all (${sorted.length})`}
          </button>
        )}
      </div>
    </DarkCard>
  );
}

// Org-level data completeness alert — cohorts missing one or more chain/KPI signals.
export function DataGapsStrip({ selectedOrg, navigate, variant = 'dark' }) {
  const [data, setData] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    const params = new URLSearchParams();
    if (selectedOrg && selectedOrg !== 'all') params.set('organization_id', selectedOrg);
    api.get(`/roe/actuals/completeness?${params.toString()}`)
      .then((res) => { if (alive) setData(res.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [selectedOrg]);
  if (!data || !data.total) return null;
  const ok = data.incomplete === 0;
  const worst = Object.entries(data.missing_counts || {}).sort((a, b) => b[1] - a[1]).filter(([, n]) => n > 0).slice(0, 3);
  const LABEL = { capability: 'Capability', confidence: 'Confidence', application: 'Application', kpi: 'KPI' };
  if (variant === 'tile') {
    return (
      <button
        onClick={() => navigate('/roe-health/cohorts')}
        className="rounded-xl border border-[#1f2a44] bg-[#111827] px-4 py-2.5 min-w-[104px] text-left hover:border-amber-500/40 transition-colors"
        title={ok ? 'Every cohort has Capability, Confidence, Application and KPI data' : `Missing most: ${worst.map(([k, n]) => `${LABEL[k]} (${n})`).join(', ')}`}
        data-testid="home-kpi-data-gaps"
      >
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Data Gaps</div>
        <div className="flex items-baseline gap-1.5">
          <span className={cn('text-xl font-heading font-bold', ok ? 'text-emerald-400' : 'text-amber-400')}>{data.incomplete}</span>
          <span className="text-[10px] text-slate-500">of {data.total} cohorts</span>
        </div>
      </button>
    );
  }
  return (
    <DarkCard className="px-5 py-4" testId="roe-data-gaps">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <span className={cn('w-8 h-8 rounded-lg ring-1 flex items-center justify-center', ok ? 'bg-emerald-500/15 ring-emerald-500/30' : 'bg-amber-500/15 ring-amber-500/30')}>
            {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-amber-400" />}
          </span>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-300">Data Completeness</p>
            <p className="text-xs text-slate-500" data-testid="roe-data-gaps-summary">
              {ok ? `All ${data.total} cohorts carry Capability, Confidence, Application and KPI data.` : `${data.incomplete} of ${data.total} cohorts are missing at least one signal.`}
            </p>
          </div>
        </div>
        {!ok && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {worst.map(([k, n]) => (
              <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border bg-amber-500/10 border-amber-500/30 text-amber-200" data-testid={`roe-data-gap-${k}`}>
                no {LABEL[k]} <span className="font-bold tabular-nums">{n}</span>
              </span>
            ))}
          </div>
        )}
        <button onClick={() => navigate('/roe-health/cohorts')} className="ml-auto text-xs font-semibold text-[#44b6d2] hover:text-cyan-300 inline-flex items-center gap-1 transition-colors" data-testid="roe-data-gaps-view">Review cohorts <ArrowRight className="h-3.5 w-3.5" /></button>
        {!ok && (
          <button onClick={() => setOpen((o) => !o)} className="text-xs font-semibold text-slate-300 hover:text-white transition-colors" data-testid="roe-data-gaps-toggle">
            {open ? 'Hide' : `Fix ${Math.min(data.cohorts?.length || 0, 12)} cohorts`}
          </button>
        )}
      </div>
      {open && !ok && (
        <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1.5" data-testid="roe-data-gaps-list">
          {(data.cohorts || []).slice(0, 12).map((c) => (
            <li key={c.cohort_id} className="flex items-center justify-between gap-2 rounded-lg border border-white/5 bg-white/[0.03] px-2.5 py-1.5" data-testid={`roe-data-gap-cohort-${c.cohort_id}`}>
              <span className="text-xs text-slate-200 truncate">{c.cohort_name}</span>
              <CompletenessBadge c={{ ...c, missing: c.missing, missing_labels: c.missing_labels, score: c.score }} cohortId={c.cohort_id} cohortName={c.cohort_name} navigate={navigate} />
            </li>
          ))}
        </ul>
      )}
    </DarkCard>
  );
}

// Quarter/period-over-period Confidence → Application → Behavior trend strip.
// Clicking a month drills into the Cohorts view scoped to that period.
function ConfidenceTrend({ selectedOrg, navigate, onDrill }) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    const params = new URLSearchParams();
    if (selectedOrg && selectedOrg !== 'all') params.set('organization_id', selectedOrg);
    api.get(`/roe/actuals/trend?${params.toString()}`)
      .then((res) => { if (alive) setData(res.data?.trend || []); })
      .catch(() => { if (alive) setData([]); });
    return () => { alive = false; };
  }, [selectedOrg]);
  if (!data || data.length < 2) return null;
  const drillable = data.some((d) => d.period);
  const handleClick = (state) => {
    const idx = state?.activeTooltipIndex;
    const row = state?.activePayload?.[0]?.payload
      || (idx != null && data[Number(idx)])
      || (state?.activeLabel != null ? data.find((d) => d.month === state.activeLabel) : null);
    if (!row) return;
    if (onDrill) return onDrill(row);
    if (row.period && navigate) navigate(`/roe-health/cohorts?period=${encodeURIComponent(row.period)}`);
  };
  return (
    <DarkCard className="p-5" testId="confidence-trend">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Transfer Trend</p>
          <p className="text-[11px] text-slate-500">Confidence → Application → Behavior over time{drillable ? ' · click a month to see its cohorts' : ''}</p>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          {[['Confidence', '#a855f7'], ['Application', '#22d3ee'], ['Behavior', '#34d399']].map(([l, c]) => (
            <span key={l} className="flex items-center gap-1 text-slate-400"><span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />{l}</span>
          ))}
        </div>
      </div>
      <div style={{ width: '100%', height: 200, cursor: drillable ? 'pointer' : 'default' }} data-testid="confidence-trend-chart">
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 12, right: 12, left: 0, bottom: 0 }} onClick={drillable ? handleClick : undefined}>
            <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={{ stroke: '#1f2a44' }} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} width={38} allowDecimals={false} />
            <Tooltip contentStyle={{ background: '#0b1220', border: '1px solid #1f2a44', borderRadius: 8, fontSize: 12 }} labelStyle={{ color: '#e2e8f0' }} labelFormatter={(l, payload) => { const p = payload?.[0]?.payload?.period; return p ? `${formatPeriod(p)} · click to drill into cohorts` : l; }} />
            <Line type="monotone" dataKey="confidence" name="Confidence" stroke="#a855f7" strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
            <Line type="monotone" dataKey="application" name="Application" stroke="#22d3ee" strokeWidth={2} dot={drillable ? { r: 3, fill: '#22d3ee', cursor: 'pointer' } : false} activeDot={{ r: 5 }} />
            <Line type="monotone" dataKey="l3" name="Behavior" stroke="#34d399" strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {drillable && (
        <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="confidence-trend-periods">
          <span className="text-[10px] uppercase tracking-wide text-slate-500 mr-1">Drill into</span>
          {data.filter((d) => d.period).map((d) => (
            <button
              key={d.period}
              type="button"
              onClick={() => handleClick({ activePayload: [{ payload: d }] })}
              className="px-2 py-0.5 rounded-full text-[11px] font-semibold border border-white/10 bg-white/5 text-slate-300 hover:bg-cyan-500/15 hover:text-cyan-200 hover:border-cyan-500/30 transition-colors"
              title={`See cohort transfer for ${formatPeriod(d.period)}`}
              data-testid={`trend-period-${d.period}`}
            >
              {formatPeriod(d.period)}
            </button>
          ))}
        </div>
      )}
    </DarkCard>
  );
}

const EVIDENCE_COLORS = { self_report: '#a855f7', manager_observation: '#22d3ee', operational: '#34d399', unknown: '#64748b' };
const EVIDENCE_LABELS = { self_report: 'Self-report', manager_observation: 'Manager obs.', operational: 'Operational', unknown: 'Other' };

// Application Transfer Gap (Potential vs Actual) + Evidence Mix donut (Phase 3).
function TransferGapEvidence({ detail, navigate }) {
  if (!detail) return null;
  const { potential, actual, gap, evidence_mix = [], has_evidence, evidence_quality, coaching } = detail;
  const pie = evidence_mix.map((e) => ({ name: EVIDENCE_LABELS[e.source] || e.source, value: e.pct, source: e.source }));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <DarkCard className="p-5" testId="transfer-gap">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1">Application Transfer Gap</p>
        <p className="text-[11px] text-slate-500 mb-4">Potential (readiness × environment) vs evidenced application</p>
        {[['Potential', potential, '#22d3ee'], ['Actual', actual, barColor(actual)]].map(([l, v, c]) => (
          <div key={l} className="mb-3">
            <div className="flex justify-between text-xs mb-1"><span className="text-slate-400">{l}</span><span className="font-bold tabular-nums text-slate-200">{Math.round(v)}%</span></div>
            <div className="h-2.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, v))}%`, background: c }} /></div>
          </div>
        ))}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border', gap > 0 ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300')}>
            {gap > 0 ? `${Math.round(gap)} pt unrealized` : `${Math.abs(Math.round(gap))} pt over-delivery`}
          </span>
          <span className="text-[11px] text-slate-500">{gap > 0 ? 'confidence isn’t fully converting to on-the-job application' : 'application exceeds readiness expectations'}</span>
        </div>
        {coaching && navigate && (
          <button onClick={() => navigate('/roe-health/priorities')} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#44b6d2] hover:text-cyan-300 transition-colors" data-testid="transfer-gap-coaching-link">
            {coaching.severity} gap — see coaching plan <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </DarkCard>
      <DarkCard className="p-5" testId="evidence-mix">
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Evidence Mix</p>
          <EvidenceQualityChip quality={evidence_quality} />
        </div>
        <p className="text-[11px] text-slate-500 mb-2">{has_evidence ? 'Where application evidence comes from' : 'Single-source (manager observation) — enable multi-source capture for a richer mix'}</p>
        <div className="flex items-center gap-4">
          <div style={{ width: 130, height: 130 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={38} outerRadius={60} paddingAngle={2} stroke="none">
                  {pie.map((e, i) => (<Cell key={i} fill={EVIDENCE_COLORS[e.source] || '#64748b'} />))}
                </Pie>
                <Tooltip contentStyle={{ background: '#0b1220', border: '1px solid #1f2a44', borderRadius: 8, fontSize: 12 }} formatter={(v) => `${v}%`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5">
            {pie.map((e, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: EVIDENCE_COLORS[e.source] || '#64748b' }} />
                <span className="text-slate-300">{e.name}</span>
                <span className="text-slate-500 tabular-nums">{e.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </DarkCard>
    </div>
  );
}

export default function RoeLeanOverview({ health, variance, roeActualPct, components, strongest, topDeficiency, watchArea, valueAtStake, selectedOrg, selectedCohort, isSuperAdmin, navigate }) {
  const rows = (variance || []).filter((r) => ROE_METRIC_KEYS[r.metric] != null);
  const worst = rows.length ? rows.reduce((lo, r) => (r.variance < lo.variance ? r : lo), rows[0]) : null;
  const metricKey = worst ? ROE_METRIC_KEYS[worst.metric] : null;
  const LIFT_POINTS = 15;
  const absVar = worst ? Math.abs(worst.variance || 0) : 0;
  const realizedPct = Math.max(0, Math.min(100, 100 - absVar));
  const afterFixPct = Math.min(100, realizedPct + LIFT_POINTS);
  const deltaPct = afterFixPct - realizedPct;
  const vas = valueAtStake?.annual_value_at_stake_usd ?? null;
  const currency = valueAtStake?.currency || 'USD';
  const financialImpact = worst ? {
    realizedPct, afterFixPct, deltaPct, currency,
    liftLabel: `Close ${worst.metric} variance by ${LIFT_POINTS}pts`,
    dollarsLeaking: vas != null ? vas * (1 - realizedPct / 100) : null,
    dollarsUnlocked: vas != null ? vas * (deltaPct / 100) : null,
    leakCopy: `Every quarter you leave a ${worst.variance > 0 ? '+' : ''}${worst.variance} pt gap between modelled and actual ${worst.metric}, roughly ${Math.round(absVar)}% of the ROE story goes unrealised.`,
    fixCopy: `Closing the ${worst.metric} variance by ${LIFT_POINTS}pts shifts realized ROE by ${Math.round(deltaPct)} percentage points.`,
    vasFootnote: valueAtStake ? { revenue: valueAtStake.inputs?.annual_revenue, marginPct: valueAtStake.inputs?.operating_margin_pct, isDefault: !!valueAtStake.is_default } : null,
  } : undefined;
  const roeFocus = worst ? {
    engine: 'roe',
    entityId: metricKey,
    entityLabel: `${worst.metric} variance`,
    pillarId: metricKey,
    score: worst.variance,
    scoreLabel: `${worst.variance > 0 ? '+' : ''}${worst.variance} pts`,
    target: 0,
    lift: LIFT_POINTS,
    overallScore: roeActualPct,
    granularity: 'component',
    granularityLabel: 'component',
    organizationId: selectedOrg && selectedOrg !== 'all' ? selectedOrg : undefined,
  } : null;
  return (
    <div className="space-y-4" data-testid="roe-overview">
      <TransferHealthChain selectedOrg={selectedOrg} selectedCohort={selectedCohort} isSuperAdmin={isSuperAdmin} />
      <KpiTiles roeActualPct={roeActualPct} components={components} strongest={strongest} topDeficiency={topDeficiency} watchArea={watchArea} peer={health?.peer_benchmark} navigate={navigate} />
      {roeFocus && <WeakestMeasureTile focus={roeFocus} onView={() => navigate('/roe-health/priorities')} testId="roe-weakest-measure-tile" />}
      <AlertsStrip deficiencies={health?.deficiencies} navigate={navigate} />
      <DataGapsStrip selectedOrg={selectedOrg} navigate={navigate} />
      <TransferGapEvidence detail={health?.application_detail} navigate={navigate} />
      <ConfidenceTrend selectedOrg={selectedOrg} navigate={navigate} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        <div className="lg:col-span-7"><ComponentBreakdown components={components} /></div>
        <DarkCard className="p-5 lg:col-span-5" testId="roe-peer">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">Peer Benchmark · ROE</div>
          {health?.peer_benchmark ? (
            <div className="space-y-2 text-sm">
              <div className="flex items-baseline gap-2"><span className="text-3xl font-extrabold tabular-nums text-cyan-300">{health.peer_benchmark.percentile}th</span><span className="text-xs text-slate-400">percentile · {health.peer_benchmark.performance_label || health.peer_benchmark.industry_name}</span></div>
              <p className="text-xs text-slate-400">You: <span className="font-semibold text-slate-200">{Math.round(roeActualPct)}%</span> · peer median <span className="font-semibold text-slate-200">{Math.round(health.peer_benchmark.peer_median)}%</span></p>
              <PeerBenchmarkBar yourValue={roeActualPct} bench={health.peer_benchmark} testId="roe-benchmark-bar" />
              <PeerBenchmarkTrend stage="behavior" organizationId={selectedOrg} testId="roe-benchmark-trend" />
            </div>
          ) : <p className="text-sm text-slate-500">No benchmark available.</p>}
        </DarkCard>
      </div>
      <DarkCard className="p-5" testId="roe-overview-action-plan">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">Action Plan</div>
        {worst ? (
          <div className="space-y-3">
            <EngineWeakLinkCallout
              entityLabel={`${worst.metric} variance`}
              score={worst.variance}
              overall={roeActualPct}
              metricSuffix="pts"
              signed
              description="has the biggest gap between modelled and actual."
              testIdPrefix="roe-weak-link"
              financialImpact={financialImpact}
            />
            <EngineActionPlanCard engine="roe" params={{ metric: metricKey, organization_id: selectedOrg, cohort_id: selectedCohort !== 'all' ? selectedCohort : undefined, score: worst.variance, overall_score: roeActualPct }} />
          </div>
        ) : <p className="text-sm text-slate-500">No variance data yet — import ROE actuals to unlock coaching.</p>}
      </DarkCard>
    </div>
  );
}
